import { MAXIMUM_CAPACITY_OF_SUPERVISED_PROJECTS_BY_SUPERVISOR } from '../definitions/constants/supervisorConstants';
import { ArchiveProjectStatus } from '../definitions/enums/archiveEnums';
import { ExternalSupervisorInvitationStatus } from '../definitions/enums/externalSupervisorInvitation';
import { ResearchAreaFormat } from '../definitions/enums/researchArea';
import {
    ArchivedProject,
    DeadlineDates,
    Department,
    ExtensionRequest,
    ExternalSupervisorInvitation,
    Faculty,
    getInitialProjectTypeSettingsState,
    Intake,
    ProfileType,
    Program,
    ProjectDates,
    ProjectModel,
    ProjectOverview,
    ProjectTimeline,
    ResearchArea,
    SupervisorGroup,
    SupervisorProposal,
    User,
    ProjectFailed,
    IArchivedProjectTimeline,
    IArchivedPlagiarismCheck,
} from '../store/types';

import { UserRole, IAdminUpdateProjectDeadlines } from 'susy-common/models';

import { ProjectStatus } from './utils';
import { ProjectTimelineType } from 'susy-common/enums';
import {
    SubmissionErrorType,
    SubmissionStatus,
} from '../definitions/enums/plagiarismCheckerEnums';

export const INITIAL_PROFILE_STATE: ProfileType = {
    profilePictureId: '',
    publications: [],
};

export const INITIAL_DEADLINE_DATES: DeadlineDates = {
    submission: null,
    evaluation: null,
};

export const INITIAL_ADMIN_DEADLINE_DATES_PAYLOAD: IAdminUpdateProjectDeadlines =
    {
        submission: null,
        evaluation: null,
        comment: '',
    };

export const INITIAL_PROJECT_OVERVIEW: ProjectOverview = {
    projects: [],
    invites: [],
};

export const INITIAL_PROJECT_DATES_STATE: ProjectDates = {
    approved: null,
    declined: null,
    handedIn: null,
    proposed: null,
    registered: null,
    saved: null,
    withdrawn: null,
    evaluated: null,
    deadlineDates: INITIAL_DEADLINE_DATES,
    failed: null,
};

export const INITIAL_INTAKE_STATE: Intake = {
    _id: '',
    name: '',
};

export const INITIAL_PROGRAM_STATE: Program = {
    _id: '',
    name: '',
};

export const INITIAL_FACULTY_STATE: Faculty = {
    _id: '',
    name: '',
};

export const INITIAL_DEPARTMENT_STATE: Department = {
    _id: '',
    name: '',
};

export const INITIAL_SUPERVISOR_GROUP_STATE: SupervisorGroup = {
    _id: '',
    name: '',
};

export const INITIAL_USER_ROLE: UserRole = {
    _id: '',
    key: '',
    name: '',
    disabled: false,
};

export const INITIAL_USER_STATE: User = {
    _id: '',
    email: '',
    firstName: '',
    lastName: '',
    intakes: [INITIAL_INTAKE_STATE],
    programs: [],
    faculties: [],
    departments: [],
    lastSeenVersion: '',
    supervisorGroups: [],
    creationDate: new Date(),
    profilePictureId: '',
    waitingDelete: false,
    isExternalSupervisor: false,
    projectsSupervisionCapacity:
        MAXIMUM_CAPACITY_OF_SUPERVISED_PROJECTS_BY_SUPERVISOR,
    userRole: INITIAL_USER_ROLE,
    permissions: [],
    topicsWithUnreadMessages: [],
};

export const INITIAL_RESEARCH_AREA_STATE: ResearchArea = {
    _id: null,
    title: '',
    format: ResearchAreaFormat.IndividualGroup,
    keywords: [],
    supervisor: INITIAL_USER_STATE,
    intakes: [],
    overview: '',
    dateCreated: new Date(),
};

export const INITIAL_SECOND_SUPERVISOR_STATE: SupervisorProposal = {
    _id: null,
    supervisorUser: INITIAL_USER_STATE,
    declineDescription: '',
    status: '',
};

export const INITIAL_PROJECT_TIMELINE_STATE: ProjectTimeline = {
    _id: '',
    name: '',
    type: ProjectTimelineType.DYNAMIC,
    definedKickoffDate: new Date(),
    definedDeadlineDate: new Date(),
    dynamicWorkingTimeUseDefaultSetting: true,
    dynamicKickoffAmountOfDays: 30,
    registrationKickoffDate: new Date(),
    registrationDeadlineDate: new Date(),
    evaluationUseDefaultSetting: true,
    evaluationAmountOfDays: 30,
    intakes: [],
};

export const INITIAL_ARCHIVED_PROJECT_TIMELINE_STATE: IArchivedProjectTimeline =
    {
        _id: '',
        name: '',
        type: ProjectTimelineType.DYNAMIC,
        definedKickoffDate: new Date(),
        definedDeadlineDate: new Date(),
        dynamicWorkingTimeUseDefaultSetting: true,
        dynamicKickoffAmountOfDays: 30,
        registrationKickoffDate: new Date(),
        registrationDeadlineDate: new Date(),
        evaluationUseDefaultSetting: true,
        evaluationAmountOfDays: 30,
    };

export const INITIAL_ARCHIVED_PROJECT_PLAGIARISM_CHECK_STATE: IArchivedPlagiarismCheck =
    {
        _id: '',
        status: SubmissionStatus.INIT,
        errorType: SubmissionErrorType.CANNOT_EXTRACT_TEXT,
        retryTimes: 0,
        errorMessage: '',
        overall_match_percentage: 0,
        internet_match_percentage: 0,
        publication_match_percentage: 0,
        submitted_works_match_percentage: 0,
    };

export const INITIAL_ARCHIVED_PROJECT_STATE: ArchivedProject = {
    _id: null,
    title: '',
    archiveProjectType: ArchiveProjectStatus.UNKNOWN,
    isVisibleToEveryone: false,
    overview: '',
    literature: [],
    dates: INITIAL_PROJECT_DATES_STATE,
    supervisor: INITIAL_USER_STATE,
    secondSupervisors: [{ ...INITIAL_USER_STATE, userId: '' }],
    taskForOwnerInGroup: '',
    teamList: [],
    proposedBy: INITIAL_USER_STATE,
    researchArea: INITIAL_RESEARCH_AREA_STATE,
    isDisclosureAgreementAttached: false,
    files: [],
    grading: [],
    intake: {
        name: '',
        _id: '',
        clmId: '',
        courses: [],
        program: {
            _id: '',
            name: '',
            faculty: {
                _id: '',
                name: '',
            },
        },
    },
    projectTypeSettings: {
        ...getInitialProjectTypeSettingsState(),
        intakes: [],
    },
    secondAttempt: {
        secondAttemptStudentIds: [],
    },
    projectTimeline: INITIAL_ARCHIVED_PROJECT_TIMELINE_STATE,
    plagiarismCheck: INITIAL_ARCHIVED_PROJECT_PLAGIARISM_CHECK_STATE,
};

export const INITIAL_PROJECT_FAILED: ProjectFailed = {
    reason: '',
    isFailedByAdmin: false,
    lastStateBeforeFailed: {
        lastStatus: ProjectStatus.not_initialized,
        gradePoints: [],
    },
};

export const INITIAL_PROJECT_STATE: ProjectModel = {
    _id: null,
    projectTypeSettings: getInitialProjectTypeSettingsState(),
    title: '',
    literature: [],
    currentStatus: ProjectStatus.not_initialized,
    dates: INITIAL_PROJECT_DATES_STATE,
    supervisor: INITIAL_USER_STATE,
    secondSupervisorProposals: [INITIAL_SECOND_SUPERVISOR_STATE],
    taskForOwnerInGroup: '',
    teamList: [],
    intake: INITIAL_INTAKE_STATE,
    proposedBy: INITIAL_USER_STATE,
    researchArea: INITIAL_RESEARCH_AREA_STATE,
    groupProject: false,
    isDisclosureAgreementAttached: false,
    files: [],
    changesReviewed: false,
    declineDescription: '',
    extensionRequests: [],
    secondAttempt: {
        adminApprovalRequired: false,
        secondAttemptStudents: [],
    },
    individualGrading: false,
    grading: [],
    projectFailed: INITIAL_PROJECT_FAILED,
};

export const INITIAL_EXTENSION_REQUEST: ExtensionRequest = {
    _id: '',
    state: null,
    sendDate: new Date(),
    receiveDate: null,
    answerDate: null,
    project: INITIAL_PROJECT_STATE,
    user: INITIAL_USER_STATE,
    fileOfProofType: null,
    fileOfProof: [],
    numberOfDaysForExtension: null,
    rejectionExplanation: null,
};

export const INITIAL_EXTERNAL_SUPERVISOR_INVITATION: ExternalSupervisorInvitation =
    {
        _id: '',
        invitationId: '',
        invitedBy: INITIAL_USER_STATE,
        project: INITIAL_PROJECT_STATE,
        status: ExternalSupervisorInvitationStatus.notInitialized,
    };
